import React from 'react';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import ServiceTypeFilter from '@rover/components/src/formFields/ServiceTypeFilter';
import { LOCATION_DEFAULT_TITLE_2 } from '@rover/constants/src/location/location.constants';
import { DROP_OFF_PICK_UP_SUPPORTED_SERVICES } from '@rover/constants/src/search/searchPage.constants';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import {
  FilterWrapper,
  Location,
} from '@rover/react-lib/src/pages/search/SearchPage/components/Filters';
import {
  DayTimeAvailability,
  DaytimeServiceScheduler,
  OvernightDateRange,
} from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import PetCounter from '@rover/react-lib/src/pages/search/SearchPage/components/PetCounter';
import { shouldShowControls } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { WizardPageType } from '@rover/react-lib/src/pages/search/SearchPage/WizardPageType';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useAvailablePets from '@rover/shared/js/hooks/useAvailablePets';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import type { ServiceType, ServiceTypeOption } from '@rover/types/src/ServiceType';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import {
  SearchInterstitialSelectDates,
  SearchInterstitialUpdateFrequency,
  SearchInterstitialUpdateLocation,
  SearchInterstitialUpdatePetType,
  SearchInterstitialUpdateServiceType,
} from './analyticsEvents';

export type Props = {
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  hasPets: boolean;
  isCatOnly: boolean;
  language: string;
  onChange: (arg0: Partial<SearchFilters>, arg1?: boolean) => void;
  pageType: WizardPageType;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  serviceTypeOptions: ServiceTypeOption[];
  locationInputId?: string;
};

const PageOneWizardCollection = ({
  getServiceTypeBySlug,
  hasPets,
  isCatOnly,
  language,
  onChange,
  pageType,
  searchFilters,
  serviceTypeFilterOptions,
  serviceTypeOptions,
  locationInputId = 'location-input-wizard',
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
  const handleChangeDateRange = (dateRange): void => {
    if (dateRange) {
      onChange(
        {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        },
        dateRange.fireSearch
      );
      emitAnalyticsEvent(new SearchInterstitialSelectDates());
    }
  };

  const handleSelectedDaysChange = (selectedDays): void => {
    onChange({ selectedDays });
  };

  const handleFrequencyChange = (frequency?: ServiceFrequencyEnum): void => {
    if (frequency) {
      emitAnalyticsEvent(
        new SearchInterstitialUpdateFrequency({
          frequency,
          serviceType: searchFilters.serviceType,
        })
      );
    }
    onChange({ frequency });
  };

  // Abstract this into a selector in https://roverdotcom.atlassian.net/browse/DEV-42261
  const startPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Drop off`
    : t`Start`;
  const endPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Pick up`
    : t`End`;

  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;

  const { showPetType, showDateRange, showDaytimeServiceScheduler, showDaytimeAvailability } =
    shouldShowControls({
      hasPets,
      isCatOnly: isCatOnly || isCatOnlyBrand,
      pageType,
      serviceOptions: serviceTypeFilterOptions,
      serviceType: serviceType || undefined,
      serviceTypeSlug: searchFilters.serviceType,
    });

  const maxDate = getMaxRequestDate();

  const availablePets = useAvailablePets(searchFilters.serviceType);

  return (
    <>
      {showPetType && pageType !== WizardPageType.MODAL && (
        <FilterWrapper>
          <PetCounter
            inputLabel={t`I'm looking for service for my:`}
            inputLabelMarginBottom="2x"
            inputLabelColor="secondary"
            dogCount={searchFilters.dogCount}
            catCount={searchFilters.catCount}
            puppyCount={searchFilters.puppyCount}
            availablePets={availablePets}
            triggeredFrom="Search"
            onChange={({ petType, dogCount, catCount, puppyCount }) => {
              const localIsCatOnly = getIsCatOnlyPetType(petType);
              const args = {
                petType,
                catCare: localIsCatOnly,
                dogCount,
                catCount,
                puppyCount,
                ...(localIsCatOnly && { serviceType: SERVICE_TYPE_CHOICES.dropIn }),
              };
              emitAnalyticsEvent(
                new SearchInterstitialUpdatePetType({
                  dog: petType.includes(PetType.DOG),
                  cat: petType.includes(PetType.CAT),
                  puppy: petType.includes(PetType.PUPPY),
                })
              );
              return onChange(args);
            }}
            hideHelperText
          />
        </FilterWrapper>
      )}
      <FilterWrapper>
        <ServiceTypeFilter
          displayMode={pageType === WizardPageType.MODAL ? 'horizontal' : null}
          serviceTypeOptions={serviceTypeOptions}
          label={t`Which service do you need?`}
          searchFilters={searchFilters}
          onChange={(newFilters) => {
            emitAnalyticsEvent(
              new SearchInterstitialUpdateServiceType({
                serviceType: newFilters.serviceType,
              })
            );
            onChange(newFilters);
          }}
        />
      </FilterWrapper>
      <FilterWrapper>
        <Location
          locationInputId={locationInputId}
          label={i18n._(LOCATION_DEFAULT_TITLE_2)}
          location={searchFilters.location}
          onChange={(newFilters) => {
            emitAnalyticsEvent(new SearchInterstitialUpdateLocation());
            onChange(newFilters);
          }}
        />
      </FilterWrapper>
      {showDateRange && (
        <FilterWrapper>
          <OvernightDateRange
            endPlaceholder={endPlaceholder}
            maxDate={maxDate}
            label={t`Which dates do you need?`}
            language={language}
            onChange={handleChangeDateRange}
            searchFilters={searchFilters}
            startPlaceholder={startPlaceholder}
          />
        </FilterWrapper>
      )}
      {showDaytimeServiceScheduler && (
        <FilterWrapper>
          <DaytimeServiceScheduler
            displayAs={pageType === WizardPageType.MODAL ? 'compact' : null}
            endPlaceholder={endPlaceholder}
            language={language}
            maxDate={maxDate}
            onChange={handleFrequencyChange}
            onDateRangeChange={handleChangeDateRange}
            oneTimeLabel={t`Which dates do you need?`}
            onSelectedDaysChange={handleSelectedDaysChange}
            onStartDateChange={handleChangeDateRange}
            repeatStartDateLabel={startPlaceholder}
            searchFilters={searchFilters}
            serviceType={serviceType}
            showArrow={false}
            showLabel
            startPlaceholder={startPlaceholder}
          />
        </FilterWrapper>
      )}
      {pageType === WizardPageType.MODAL && showDaytimeAvailability && (
        <FilterWrapper>
          <DayTimeAvailability
            availabilityBuckets={{
              morning: morningAvailability ? morningAvailability.label : null,
              midday: middayAvailability ? middayAvailability.label : null,
              evening: eveningAvailability ? eveningAvailability.label : null,
            }}
            label={t`Which time(s) do you need?`}
            labelIsVisible
            onChange={(availability) => onChange(availability)}
            searchFilters={searchFilters}
          />
        </FilterWrapper>
      )}
    </>
  );
};

export default PageOneWizardCollection;
